import React, { Fragment, FunctionComponent, useEffect } from "react";

import { CLOSE_ALL_OVERLAYS } from "layout/redux/overlay.actions";
import { Helmet } from "react-helmet-async";
import { PAGE_TYPE } from "./page.types";
import { thunkUpdatePage } from "../redux/page.actions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { AppThunkDispatch } from "app/redux-state.interface";
import AppLocationState from "app/models/location-state.interface";
import CategoryPage from "pages/Catalog/pages/CategoryPage/CategoryPage";
import ContentPage from "pages/ContentPage/ContentPage";
import ErrorPage from "pages/ErrorPage/ErrorPage";
import getPageMeta from "../helpers/getPageMeta";
import HomePage from "pages/HomePage/HomePage";
import isBrowserContext from "helpers/isBrowserContext";
import MediaCloudPage from "pages/Catalog/pages/MediaCloudPage/MediaCloudPage";
import NotFoundPage from "pages/NotFoundPage/NotFoundPage";
import ProductPage from "pages/Catalog/pages/ProductPage/ProductPage";

const UmbracoPage: FunctionComponent = () => {
    const page = useSelector((state: any) => state.page.content);
    const settings = useSelector((state: any) => state.settings.content);

    // TODO: Declare type for UmbracoPage thunk actions
    const dispatch = useDispatch<AppThunkDispatch>();
    const navigate = useNavigate();
    const location = useLocation();

    // Page update - request
    useEffect(() => {
        // TODO: Dispatch UpdatePageRequest to show a loader while waiting (show if >250ms and show for minimum 250ms to avoid flashes.)
        // TODO: Use the UpdatePageRequest to check if history.back was used, and then fetch previous page synchronous from store (for scroll restoration)
        dispatch(thunkUpdatePage(location.pathname, navigate, location));
    }, [location.pathname]);

    // Page update - callback
    useEffect(() => {
        if (page?._url || page?._contentType === "category" || page?._contentType === "product") {
            // #SCROLL_RESTORATION: Resetting scrollbar on page changes to top page
            // if (isBrowserContext()) {
            //     document.querySelector("body")?.scrollTo({
            //         left: 0,
            //         top: history.location.state?.scrollY ?? 0,
            //         behavior: "instant", // But it is working
            //     } as any);
            // }

            // #OVERLAY: Reset the redux drawer state to None when changing to a new page
            if (page?._url) {
                dispatch({
                    type: CLOSE_ALL_OVERLAYS,
                });
            }
        }
    }, [page]);

    useEffect(() => {
        if (!isBrowserContext()) return;

        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "auto",
        });
    }, [location.pathname]);

    const renderHead = (page: PAGE_TYPE) => {
        // TODO: Open Graph
        // TODO: Canonical
        const { language, metaTitle, metaDescription, metaKeywords, metaCanonical, robots, socialTitle, socialDescription, socialImage, url } = getPageMeta(
            page,
            settings
        );

        return (
            <Helmet>
                <html lang={language} />
                <title>{metaTitle}</title>

                <meta
                    name="title"
                    content={metaTitle}
                />
                <meta
                    name="description"
                    content={metaDescription}
                />
                <meta
                    name="keywords"
                    content={metaKeywords}
                />
                <meta
                    name="canonical"
                    content={metaCanonical}
                />
                <meta
                    name="robots"
                    content={robots}
                />

                <meta
                    property="og:title"
                    content={socialTitle}
                />
                <meta
                    property="og:description"
                    content={socialDescription}
                />
                <meta
                    property="og:image"
                    content={socialImage}
                />
                <meta
                    property="og:locale"
                    content={language}
                />
                <meta
                    property="og:site_name"
                    content="danishbakery"
                />
                <meta
                    property="og:type"
                    content="website"
                />
                <meta
                    property="og:url"
                    content={url}
                />

                <meta
                    property="og:url"
                    content={url}
                />

                {page?._hrefLang &&
                    Object.keys(page._hrefLang).map((hrefLang: any, index: number) => (
                        <Fragment key={index}>
                            <link
                                rel="alternate"
                                hrefLang={hrefLang}
                                href={(page._hrefLang as any)[hrefLang]}
                            />
                        </Fragment>
                    ))}
            </Helmet>
        );
    };

    const renderBody = (page: PAGE_TYPE) => {
        // TODO: Schema JSON-LD
        // TODO: Schema Breadcrumb

        switch (page?._contentType) {
            case "homePage": {
                return <HomePage {...page} />;
            }
            case "contentPage": {
                return <ContentPage {...page} />;
            }
            case "category": {
                return <CategoryPage {...page} />;
            }
            case "product": {
                return <ProductPage {...page} />;
            }
            case "mediaCloudPage": {
                return <MediaCloudPage {...page} />;
            }
            case "notFoundPage": {
                return <NotFoundPage />;
            }
            case "errorPage": {
                return <ErrorPage />;
            }
            default: {
                return <Fragment></Fragment>;
            }
        }
    };

    return (
        <Fragment>
            {renderHead(page)}

            <div className="content">{renderBody(page)}</div>
        </Fragment>
    );
};

export default UmbracoPage;
